import React, { useEffect } from 'react';
import { X, ZoomIn, ZoomOut, Maximize2, Share2 } from 'lucide-react';
import { theme } from '../../theme';
import { useImageZoomPan } from './useImageZoomPan';

export interface ImageViewerModalProps {
  isOpen: boolean;
  /** Full-size image URL */
  src: string;
  /** Accessible name / caption for the image */
  alt: string;
  onClose: () => void;
  /** Optional caption shown under the toolbar (defaults to alt) */
  caption?: string;
  zIndex?: number;
  /** When provided, renders a Share button in the toolbar */
  onShare?: () => void;
}

const toolbarButtonStyle: React.CSSProperties = {
  background: 'rgba(30, 30, 30, 0.9)',
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: 8,
  color: theme.colors.text.primary,
  width: 40,
  height: 40,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * The app's single full-size image viewer. Every surface that opens an image
 * full-screen (service photos, work order photos, vehicle thumbnails, failed
 * VIN scans, ...) renders this component so zooming behaves the same way
 * everywhere:
 * - mouse wheel / trackpad zoom anchored at the cursor
 * - pinch zoom on touch devices
 * - drag to pan while zoomed
 * - double-click / double-tap toggles zoom
 * - toolbar with zoom in / out / reset and the current zoom level
 * - Escape or backdrop click closes
 */
export const ImageViewerModal: React.FC<ImageViewerModalProps> = ({ isOpen, src, alt, onClose, caption, zIndex = 10100, onShare }) => {
  const zoom = useImageZoomPan();
  const { reset } = zoom;

  // A new image (or a fresh open) always starts un-zoomed.
  useEffect(() => {
    reset();
  }, [src, isOpen, reset]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-label={alt}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Pinch/drag must reach our pointer handlers, not scroll the page.
        touchAction: 'none',
        overscrollBehavior: 'contain',
      }}
      onClick={() => {
        // A click that ends a pan drag must not close the viewer.
        if (!zoom.consumeDidPan()) onClose();
      }}
      {...zoom.containerHandlers}
    >
      {/* Toolbar */}
      <div
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          zIndex: 1,
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <div
          aria-live="polite"
          style={{
            color: theme.colors.text.secondary,
            fontSize: 12,
            fontVariantNumeric: 'tabular-nums',
            background: 'rgba(30, 30, 30, 0.9)',
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: 8,
            padding: '0 10px',
            height: 40,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {Math.round(zoom.scale * 100)}%
        </div>
        <button onClick={zoom.zoomOut} title="Zoom out" aria-label="Zoom out" style={toolbarButtonStyle}>
          <ZoomOut size={18} />
        </button>
        <button onClick={zoom.zoomIn} title="Zoom in" aria-label="Zoom in" style={toolbarButtonStyle}>
          <ZoomIn size={18} />
        </button>
        <button
          onClick={zoom.reset}
          title="Reset zoom"
          aria-label="Reset zoom"
          disabled={!zoom.isZoomed}
          style={{ ...toolbarButtonStyle, opacity: zoom.isZoomed ? 1 : 0.4, cursor: zoom.isZoomed ? 'pointer' : 'default' }}
        >
          <Maximize2 size={18} />
        </button>
        {onShare && (
          <button onClick={onShare} title="Share" aria-label="Share image" style={toolbarButtonStyle}>
            <Share2 size={18} />
          </button>
        )}
        <button onClick={onClose} title="Close" aria-label="Close image viewer" style={toolbarButtonStyle}>
          <X size={18} />
        </button>
      </div>

      {/* Caption */}
      {(caption ?? alt) && (
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            color: theme.colors.text.secondary,
            fontSize: 12,
            background: 'rgba(0, 0, 0, 0.6)',
            borderRadius: 6,
            padding: '4px 12px',
            maxWidth: '80%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            pointerEvents: 'none',
          }}
        >
          {caption ?? alt}
        </div>
      )}

      <img
        src={src}
        alt={alt}
        crossOrigin="anonymous"
        draggable={false}
        style={{
          // Never distort: the image keeps its intrinsic aspect ratio at
          // every zoom level. width/height stay auto (only max-constrained),
          // and zooming applies one uniform scale factor to both axes.
          width: 'auto',
          height: 'auto',
          maxWidth: '90%',
          maxHeight: '90%',
          objectFit: 'contain',
          borderRadius: zoom.isZoomed ? 0 : 8,
          transform: zoom.transform,
          transformOrigin: 'center center',
          cursor: zoom.isZoomed ? 'grab' : 'zoom-in',
          userSelect: 'none',
        }}
      />
    </div>
  );
};

export default ImageViewerModal;
